/**
 * Notification Utility Wrapper
 * 
 * Provides a consistent interface for showing toast notifications
 * throughout the application using the global configuration.
 */

import toast, { type Toast } from 'react-hot-toast';
import { notificationConfig, notificationIcons } from './notification-config';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X as XIcon } from 'lucide-react';

type NotificationVariant = keyof typeof notificationConfig.variants;

// Colliers brand palette (sourced from public/colliers-logo.svg). Used
// to render every notify.* toast in a Colliers-branded shell —
// multicolor logo on a white panel + brand-tinted gradient body.
// Success keeps a conventional green so the success cue still reads
// instantly.
const BRAND = {
  navy: '#25408f',
  navyDark: '#1a2e69',
  red: '#ed1b34',
  redDark: '#b51325',
  yellow: '#ffd400',
  yellowDark: '#caa800',
  green: '#10a663',
  greenDark: '#0a7a48',
} as const;

type BrandToastType = 'success' | 'error' | 'warning' | 'info';

const BRAND_TOAST: Record<
  BrandToastType,
  {
    gradient: string;
    border: string;
    progress: string;
    fg: string;
    fgMuted: string;
    divider: string;
    Icon: typeof CheckCircle2;
  }
> = {
  success: {
    gradient: `linear-gradient(135deg, ${BRAND.green} 0%, ${BRAND.greenDark} 100%)`,
    border: BRAND.greenDark,
    progress: 'rgba(255,255,255,0.85)',
    fg: '#fff',
    fgMuted: 'rgba(255,255,255,0.9)',
    divider: 'rgba(255,255,255,0.3)',
    Icon: CheckCircle2,
  },
  error: {
    gradient: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.redDark} 100%)`,
    border: BRAND.redDark,
    progress: 'rgba(255,255,255,0.85)',
    fg: '#fff',
    fgMuted: 'rgba(255,255,255,0.9)',
    divider: 'rgba(255,255,255,0.3)',
    Icon: XCircle,
  },
  warning: {
    gradient: `linear-gradient(135deg, ${BRAND.yellow} 0%, ${BRAND.yellowDark} 100%)`,
    border: BRAND.yellowDark,
    progress: BRAND.navy,
    fg: BRAND.navyDark,
    fgMuted: 'rgba(26,46,105,0.8)',
    divider: 'rgba(26,46,105,0.3)',
    Icon: AlertTriangle,
  },
  info: {
    gradient: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
    border: BRAND.navyDark,
    progress: 'rgba(255,255,255,0.85)',
    fg: '#fff',
    fgMuted: 'rgba(255,255,255,0.9)',
    divider: 'rgba(255,255,255,0.3)',
    Icon: Info,
  },
};

function BrandProgressBar({ duration, color }: { duration: number; color: string }) {
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    if (duration === Infinity) return;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining === 0) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, [duration]);
  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(0,0,0,0.18)' }}>
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: color,
          transition: 'width 60ms linear',
        }}
      />
    </div>
  );
}

/**
 * Branded toast body. Used by notify.success/error/warning/info AND
 * by useToast() (via showBrandedToast) to give every toast the
 * Colliers logo panel + brand-colored gradient. `react-hot-toast`
 * drives visibility via `t.visible` and dismiss via `onDismiss`;
 * this component is pure markup.
 */
function BrandedToastBody({
  variant,
  title,
  body,
  visible,
  onDismiss,
  duration,
}: {
  variant: BrandToastType;
  title: string | ReactNode;
  body?: string | ReactNode;
  visible: boolean;
  onDismiss: () => void;
  duration: number;
}) {
  const cfg = BRAND_TOAST[variant];
  const Icon = cfg.Icon;

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        minWidth: 360,
        maxWidth: 480,
        borderRadius: 12,
        overflow: 'hidden',
        background: cfg.gradient,
        border: `1px solid ${cfg.border}`,
        boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
        color: cfg.fg,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0) scale(1)' : 'translateX(20px) scale(0.97)',
        transition: 'opacity 250ms ease, transform 250ms ease',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          background: '#fff',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 0 12px rgba(0,0,0,0.05)',
        }}
      >
        <img
          src="/colliers-logo.svg"
          alt="Colliers"
          style={{
            width: 56,
            height: 56,
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))',
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: '14px 36px 14px 16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingBottom: 6,
            borderBottom: `1px solid ${cfg.divider}`,
          }}
        >
          <Icon size={18} color={cfg.fgMuted} style={{ flexShrink: 0 }} />
          <strong
            style={{
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 0.2,
              color: cfg.fg,
              wordBreak: 'break-word',
            }}
          >
            {title}
          </strong>
        </div>
        {body ? (
          <span style={{ fontSize: 13, color: cfg.fgMuted, lineHeight: 1.45 }}>{body}</span>
        ) : null}
      </div>

      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'transparent',
          border: 'none',
          color: cfg.fgMuted,
          cursor: 'pointer',
          padding: 4,
          borderRadius: 4,
          display: 'flex',
        }}
      >
        <XIcon size={16} />
      </button>

      {duration !== Infinity && <BrandProgressBar duration={duration} color={cfg.progress} />}
    </div>
  );
}

function showBranded(variant: BrandToastType, message: string | ReactNode, duration?: number) {
  return showBrandedToast(variant, message, undefined, { duration });
}

/** A button on a toast (e.g. Retry): clicking it closes the toast, then runs `onClick`. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/** A toast with an action button stays until it is clicked or closed. */
function showBrandedWithAction(variant: BrandToastType, message: string | ReactNode, action: ToastAction) {
  const id: string = showBrandedToast(
    variant,
    message,
    <button
      type="button"
      onClick={() => {
        toast.dismiss(id);
        action.onClick();
      }}
      style={{
        marginTop: 4,
        padding: '3px 10px',
        borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.7)',
        background: 'rgba(255,255,255,0.15)',
        color: 'inherit',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {action.label}
    </button>,
    { persistent: true },
  );
  return id;
}

/**
 * Lower-level branded-toast launcher. Accepts a separate title and
 * optional message body, plus persistence/duration. Used by the
 * useToast() shim in ToastContext to render every show* call through
 * the same react-hot-toast pipeline as notify.*.
 */
export function showBrandedToast(
  variant: BrandToastType,
  title: string | ReactNode,
  body?: string | ReactNode,
  options?: { duration?: number; persistent?: boolean }
) {
  const persistent = options?.persistent ?? false;
  const actualDuration = persistent
    ? Infinity
    : options?.duration ?? notificationConfig.duration;
  return toast.custom(
    (t: Toast) => (
      <BrandedToastBody
        variant={variant}
        title={title}
        body={body}
        visible={t.visible}
        onDismiss={() => toast.dismiss(t.id)}
        duration={actualDuration}
      />
    ),
    { duration: actualDuration }
  );
}

/**
 * Progress Bar Component for Toast Notifications
 */
const ProgressBar = ({ duration, color }: { duration: number; color: string }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (duration === Infinity) return; // Don't show progress for infinite duration

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [duration]);

  if (duration === Infinity) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `${color}20`, // 20% opacity background
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: color,
          transition: 'width 0.1s linear',
        }}
      />
    </div>
  );
};

/**
 * Toast wrapper with progress bar
 */
const ToastWithProgress = ({ 
  children, 
  variant, 
  duration = notificationConfig.duration 
}: { 
  children: ReactNode; 
  variant: NotificationVariant;
  duration?: number;
}) => {
  const variantStyle = notificationConfig.variants[variant];
  const color = variantStyle.color as string;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '8px' }}>
      {children}
      <ProgressBar duration={duration} color={color} />
    </div>
  );
};

/**
 * Main notification utility object
 */
export const notify = {
  /**
   * Show a success notification (branded toast).
   */
  success: (message: string | ReactNode) => showBranded('success', message),

  /**
   * Show an error notification (branded toast). With `action` (e.g. Retry) it stays until clicked or closed.
   */
  error: (message: string | ReactNode, options?: { action?: ToastAction }) =>
    options?.action ? showBrandedWithAction('error', message, options.action) : showBranded('error', message),

  /**
   * Show a warning notification (branded toast).
   */
  warning: (message: string | ReactNode) => showBranded('warning', message),

  /**
   * Show an info notification (branded toast).
   */
  info: (message: string | ReactNode) => showBranded('info', message),

  /**
   * Show a loading notification (useful for async operations)
   */
  loading: (message: string | ReactNode) => {
    return toast.custom(
      (t: Toast) => (
        <ToastWithProgress variant="loading" duration={Infinity}>
          <div
            style={{
              ...notificationConfig.baseStyle,
              ...notificationConfig.variants.loading,
              opacity: t.visible ? 1 : 0,
              transition: `opacity ${notificationConfig.animation.enter}ms ease-in-out`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>{notificationIcons.loading}</span>
            {message}
          </div>
        </ToastWithProgress>
      ),
      { duration: Infinity }
    );
  },

  /**
   * Show a custom notification with any variant styling
   */
  custom: (content: ReactNode, variant: NotificationVariant = 'info', duration?: number) => {
    const actualDuration = duration ?? notificationConfig.duration;
    return toast.custom(
      (t: Toast) => (
        <ToastWithProgress variant={variant} duration={actualDuration}>
          <div
            style={{
              ...notificationConfig.baseStyle,
              ...notificationConfig.variants[variant],
              opacity: t.visible ? 1 : 0,
              transition: `opacity ${notificationConfig.animation.enter}ms ease-in-out`,
            }}
          >
            {content}
          </div>
        </ToastWithProgress>
      ),
      {
        duration: actualDuration,
      }
    );
  },

  /**
   * Show a promise-based notification (great for async operations)
   * Automatically shows loading, success, and error states
   */
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string | ReactNode;
      success: string | ReactNode | ((data: T) => string | ReactNode);
      error: string | ReactNode | ((error: any) => string | ReactNode);
    }
  ) => {
    // react-hot-toast's promise() expects flat loading/success/error
    // values, not nested objects. The earlier nested shape was a
    // mis-port of the toast/sonner API; restored to the canonical
    // shape so the function actually compiles AND fires the right
    // messages in each phase.
    // Cast at the boundary: our public signature accepts the broader
    // ReactNode shape (string + any element) so callers don't need to
    // know about react-hot-toast's narrower `Renderable`. The runtime
    // accepts strings + JSX equally; the cast is type-system-only.
    return toast.promise(
      promise,
      messages as any,
      {
        style: notificationConfig.baseStyle,
        loading: {
          style: {
            ...notificationConfig.baseStyle,
            ...notificationConfig.variants.loading,
          },
          icon: notificationIcons.loading,
        },
        success: {
          style: {
            ...notificationConfig.baseStyle,
            ...notificationConfig.variants.success,
          },
          icon: notificationIcons.success,
          duration: notificationConfig.duration,
        },
        error: {
          style: {
            ...notificationConfig.baseStyle,
            ...notificationConfig.variants.error,
          },
          icon: notificationIcons.error,
          duration: notificationConfig.duration,
        },
      }
    );
  },

  /**
   * Dismiss a specific toast
   */
  dismiss: (toastId?: string) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },

  /**
   * Remove all toasts
   */
  removeAll: () => {
    toast.remove();
  },

  /**
   * Show a confirmation dialog as a toast
   * Returns a promise that resolves to true if confirmed, false if cancelled
   */
  confirm: (message: string, options?: {
    confirmText?: string;
    cancelText?: string;
    variant?: NotificationVariant;
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      const variantStyle = notificationConfig.variants[options?.variant || 'warning'];
      const variantColor = variantStyle.color as string;
      
      const toastId = toast.custom(
        (t: Toast) => (
          <div
            style={{
              ...notificationConfig.baseStyle,
              ...variantStyle,
              opacity: t.visible ? 1 : 0,
              transition: `opacity ${notificationConfig.animation.enter}ms ease-in-out`,
            }}
          >
            <div style={{ marginBottom: '12px' }}>{message}</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  toast.dismiss(toastId);
                  resolve(false);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: `1px solid ${variantColor}`,
                  background: 'transparent',
                  color: variantColor,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {options?.cancelText || 'Cancel'}
              </button>
              <button
                onClick={() => {
                  toast.dismiss(toastId);
                  resolve(true);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: `1px solid ${variantColor}`,
                  background: variantColor,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {options?.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity, // Don't auto-dismiss confirmations
        }
      );
    });
  },
};

/**
 * Export the underlying toast object for advanced usage
 */
export { toast };

/**
 * Helper function to show API error messages
 */
export function notifyApiError(error: any) {
  const message = error?.response?.data?.message || 
                  error?.message || 
                  'An unexpected error occurred';
  
  notify.error(message);
}

/**
 * Helper function for success messages with optional details
 */
export function notifySuccess(title: string, details?: string) {
  if (details) {
    notify.custom(
      <div>
        <strong>{title}</strong>
        {details && <p style={{ marginTop: '4px', fontSize: '12px', opacity: 0.9 }}>{details}</p>}
      </div>,
      'success'
    );
  } else {
    notify.success(title);
  }
}